import path from 'path';

import type { DocOpts } from '@wikibonsai/almanac';
import * as caml from 'caml-mkdn';
import yaml from 'js-yaml';


// doc opts helpers

/** CLI overrides: prefix wins over attr over path (last write wins). */
export function mergeDocOpts(base: DocOpts, argv: any): DocOpts {
  const merged: DocOpts = { ...base };
  if (argv.path !== undefined) {
    merged.path = argv.path as string;
  }
  if (argv.prefix !== undefined) {
    merged.prefix = argv.prefix as string;
  }
  return merged;
}

/** If manifest explicitly sets `attrs` and it is empty, do not emit type frontmatter. */
export function shouldEmitTypeFrontmatter(base: DocOpts): boolean {
  const attrs: unknown = (base as { attrs?: unknown }).attrs;
  if (attrs === undefined) {
    return true;
  }
  if (Array.isArray(attrs)) {
    return attrs.length > 0;
  }
  if (typeof attrs === 'string') {
    return attrs.trim().length > 0;
  }
  return false;
}

export function optsDifferFromBase(base: DocOpts, merged: DocOpts): boolean {
  if (merged.path !== base.path) {
    return true;
  }
  if (merged.prefix !== base.prefix) {
    return true;
  }
  return false;
}

// path helpers

/** Map manifest path (e.g. `/bonsai/entries/`, `/`) to a directory under cwd. */
export function dirFromDocPath(pathStr: string | undefined, cwd: string): string {
  if (pathStr === undefined || pathStr === '' || pathStr === '/') {
    return cwd;
  }
  const trimmed: string = pathStr.replace(/^\/+/, '').replace(/\/+$/, '');
  return path.join(cwd, trimmed);
}

// content helpers

/**
 * Parse repeatable --attr key=value pairs into a record.
 * Duplicate keys accumulate into arrays.
 * e.g. ['title=My Note', 'date=2026-01-01', 'tags=a', 'tags=b']
 *   → { title: 'My Note', date: '2026-01-01', tags: ['a', 'b'] }
 */
export function parseCliAttrs(raw: unknown): Record<string, unknown> {
  if (raw === undefined) { return {}; }
  const items: string[] = Array.isArray(raw) ? raw.map(String) : [String(raw)];
  const result: Record<string, unknown> = {};
  for (const item of items) {
    const eqIdx: number = item.indexOf('=');
    if (eqIdx === -1) {
      throw new Error(`invalid --attr: missing "=" in "${item}"`);
    }
    const key: string = item.substring(0, eqIdx).trim();
    const value: string = item.substring(eqIdx + 1).trim();
    if (!key) {
      throw new Error(`invalid --attr: empty key in "${item}"`);
    }
    // duplicate keys → array
    if (key in result) {
      const existing = result[key];
      if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        result[key] = [existing, value];
      }
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function cliBody(argv: any): string {
  const b: unknown = argv?.body;
  return typeof b === 'string' ? b : '';
}

export type AttrFormat = 'caml' | 'yaml';

export interface BuildNoteOpts {
  format?: AttrFormat;
  camlPrefix?: boolean;
  camlSpace?: 'pretty' | 'pad' | 'none';
  camlList?: 'mkdn' | 'comma';
}

/**
 * Build file text: attrs (CAML or YAML frontmatter) + prose body.
 * Defaults to CAML when no format is specified (matches trug config default).
 */
export function buildNoteContent(
  fm: Record<string, unknown>,
  prose: string,
  opts?: BuildNoteOpts,
): string {
  if (Object.keys(fm).length === 0) {
    return prose;
  }
  const format: AttrFormat = opts?.format ?? 'caml';
  if (format === 'yaml') {
    const header: string = yaml.dump(fm, { lineWidth: -1, noRefs: true, sortKeys: true });
    return `---\n${header}---\n\n${prose}`;
  }
  // caml (default)
  const header: string = caml.dump(fm, {
    format: opts?.camlSpace ?? 'pretty',
    listFormat: opts?.camlList ?? 'mkdn',
    prefix: opts?.camlPrefix ?? true,
  });
  return header + (prose ? '\n' + prose : '');
}
